import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Divider from '@mui/material/Divider'
import Box from '@mui/material/Box'

const participantLabel = (participant) => {
  if (!participant) return ''
  const name = [participant.firstname, participant.lastname].filter(Boolean).join(' ').trim()
  return name || participant.email || participant.id
}

/**
 * Asks the activity's extra-info questions when an admin adds participants by hand.
 * A customer enrolling through the webshop answers these themselves; adding someone
 * from here would otherwise skip the questions entirely and leave a participant with
 * a mandatory answer missing.
 *
 * Every configured question is shown, but only `required: true` ones block saving --
 * the optional answers are worth collecting while the dialog is open anyway.
 */
function ActivityParticipantExtraInfoDialog({ open, questions, participants, saving, onCancel, onConfirm }) {
  const rows = React.useMemo(() => (questions || []).filter((q) => q && q.id), [questions])
  const people = React.useMemo(() => participants || [], [participants])

  // { [participantId]: { [questionId]: answer } }
  const [answers, setAnswers] = React.useState({})

  React.useEffect(() => {
    if (open) setAnswers({})
  }, [open])

  const setAnswer = (participantId, questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [participantId]: { ...(prev[participantId] || {}), [questionId]: value },
    }))
  }

  const missingRequired = people.some((participant) =>
    rows.some((question) => {
      if (!question.required) return false
      const given = answers[participant.id] ? answers[participant.id][question.id] : ''
      return !given || !String(given).trim()
    })
  )

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth>
      <DialogTitle>Lisätiedot</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Aktiviteetti kysyy ilmoittautuessa lisätietoja. Täytä ne lisättäville osallistujille.
        </Typography>
        {people.map((participant, index) => (
          <Box key={participant.id} sx={{ mb: 2 }}>
            {index > 0 ? <Divider sx={{ mb: 2 }} /> : null}
            <Typography variant='subtitle2' gutterBottom>
              {participantLabel(participant)}
            </Typography>
            {rows.map((question) => (
              <TextField
                key={question.id}
                label={question.title + (question.required ? ' *' : '')}
                variant='standard'
                margin='dense'
                fullWidth
                value={answers[participant.id] ? answers[participant.id][question.id] || '' : ''}
                onChange={(e) => setAnswer(participant.id, question.id, e.target.value)}
              />
            ))}
          </Box>
        ))}
        {missingRequired ? (
          <Typography variant='caption' color='text.secondary'>
            Tähdellä merkityt ovat pakollisia.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Peruuta
        </Button>
        <Button
          onClick={() => onConfirm(answers)}
          variant='contained'
          color='primary'
          disabled={missingRequired || saving}
        >
          {saving ? 'Lisätään...' : 'Lisää osallistujat'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ActivityParticipantExtraInfoDialog
